import React, { useEffect, useRef, useState } from 'react';
import { FaRegSave } from 'react-icons/fa';
import { IoAddCircleOutline, IoEllipsisHorizontal, IoEllipsisVertical, IoShareOutline } from 'react-icons/io5';
import { inAppBrowserName, iosBrowser, isMobileDevice } from '../utils/installPrompt';

/**
 * Tells a guest how to add the page to their Home Screen when the browser cannot do it from a button.
 *   ios      - iPhone / iPad: the Share menu
 *   in-app   - a web view inside another app: open the page in a real browser first
 *   manual   - any other browser: its own menu
 *   install-pending - an earlier installation has not completed: wait or reopen the page
 */

const Step = ({ number, icon, children }) => (
  <li className="flex items-start gap-3 text-left">
    <span
      className="flex-none w-9 h-9 rounded-lg bg-gray-100 text-blue-600 flex items-center justify-center text-2xl"
      aria-hidden="true"
    >
      {icon}
    </span>
    <span className="text-sm text-gray-800 pt-1">
      {number ? <span className="font-semibold">{number}. </span> : null}
      {children}
    </span>
  </li>
);

const IosSteps = () => {
  const browser = iosBrowser();
  return (
    <ol className="space-y-3" aria-label="Steps">
      <Step number={1} icon={<IoShareOutline />}>
        Tap the <strong>Share</strong> button.{' '}
        <span className="text-gray-500">
          {browser === 'Safari'
            ? 'It is at the bottom of the screen (at the top on an iPad).'
            : `In ${browser} it is next to the address bar or in the menu. If you cannot find it, open this page in Safari.`}
        </span>
      </Step>
      <Step number={2} icon={<IoAddCircleOutline />}>
        Scroll down and tap <strong>Add to Home Screen</strong>.
      </Step>
      <Step number={3} icon={<span className="text-sm font-semibold">Add</span>}>
        Tap <strong>Add</strong> in the corner. Then open it from your Home Screen.
      </Step>
    </ol>
  );
};

// Phones and tablets go through the browser's menu; on a computer it depends on the browser, so those are options, not steps
const ManualSteps = () => (isMobileDevice() ? (
  <ol className="space-y-3" aria-label="Steps">
    <Step number={1} icon={<IoEllipsisVertical />}>
      Open your browser&apos;s <strong>menu</strong> (the three dots).
    </Step>
    <Step number={2} icon={<IoAddCircleOutline />}>
      Tap <strong>Add to Home screen</strong> or <strong>Install app</strong>.
    </Step>
  </ol>
) : (
  <ul className="space-y-3" aria-label="Options">
    <Step icon={<IoAddCircleOutline />}>
      <strong>Chrome or Edge:</strong> click the install icon at the right end of the address bar, or open the menu and choose <strong>Install</strong>.
    </Step>
    <Step icon={<IoShareOutline />}>
      <strong>Safari:</strong> choose <strong>File</strong>, then <strong>Add to Dock</strong>.
    </Step>
  </ul>
));

const InAppSteps = ({ appName }) => {
  const [copied, setCopied] = useState(null); // null | 'copied' | 'failed'
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied('copied');
    } catch (error) {
      setCopied('failed');
    }
  };
  return (
    <>
      <p className="mb-3 text-sm text-gray-700">
        {appName === 'this app' ? 'This app' : appName} opens pages in its own viewer, which cannot add them to your Home Screen.
      </p>
      <ol className="space-y-3" aria-label="Steps">
        <Step number={1} icon={<IoEllipsisHorizontal />}>
          Open the <strong>menu</strong> of this viewer and choose <strong>Open in Safari</strong> or <strong>Open in browser</strong>.
        </Step>
        <Step number={2} icon={<FaRegSave />}>
          In the browser, tap <strong>Save me</strong> again.
        </Step>
      </ol>
      <button
        type="button"
        className="mt-4 px-3 py-2 border border-blue-500 text-blue-600 rounded text-sm"
        onClick={copyLink}
      >
        Copy link
      </button>
      <p className="mt-2 text-xs min-h-[1rem]" role="status">
        {copied === 'copied' && 'Link copied. Paste it into your browser.'}
        {copied === 'failed' && 'Could not copy. Press and hold the address to copy it instead.'}
      </p>
    </>
  );
};

const TITLES = {
  ios: 'Add to your Home Screen',
  manual: 'Add to your Home Screen',
  'in-app': 'Open this page in your browser',
  'install-pending': 'Installation in progress',
};

const InstallGuide = ({ mode, onClose }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);

  // Keyboard and screen-reader users: move focus into the dialog, give it back when it closes.
  useEffect(() => {
    const before = document.activeElement;
    // Without preventScroll a card taller than the screen would open already scrolled down to this button, hiding the steps
    if (closeRef.current) closeRef.current.focus({ preventScroll: true });
    return () => { if (before && typeof before.focus === 'function') before.focus(); };
  }, []);

  // Escape closes the help wherever focus is: tapping plain text in the card moves focus away from its buttons
  useEffect(() => {
    const onEscape = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [onClose]);

  // Tab stays inside the dialog.
  const onKeyDown = (event) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      // Above the notification prompt (z-60), whichever opened first
      className="fixed inset-0 z-[70] p-4 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
      onKeyDown={onKeyDown}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="install-guide-title"
        tabIndex={-1}
        // A short screen or enlarged system text makes the steps taller than the screen: the card scrolls instead of running off it
        className="bg-white rounded-lg p-5 w-full max-w-sm max-h-full overflow-y-auto overscroll-contain outline-none"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="install-guide-title" className="text-lg font-semibold mb-4 text-gray-900">{TITLES[mode] || TITLES.manual}</h2>
        {mode === 'ios' && <IosSteps />}
        {mode === 'in-app' && <InAppSteps appName={inAppBrowserName() || 'this app'} />}
        {mode === 'install-pending' && (
          <p className="text-sm text-gray-700" role="status">
            Your previous installation has not finished yet. Wait for it to finish, then tap Save me again.
            If it stays stuck, close and reopen this page before trying again.
          </p>
        )}
        {mode !== 'ios' && mode !== 'in-app' && mode !== 'install-pending' && <ManualSteps />}
        <div className="mt-5 text-right">
          <button ref={closeRef} type="button" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallGuide;
